import { skipToken, useQuery } from '@tanstack/react-query';
import {
  Default_Site_Read_Nested,
  Default_Sites_Grouped_Read,
  getV1CompaniesCompanyIdOrdersOrderIdDefaultSitesMerged,
  getV1CompaniesCompanyIdProjectsProjectIdDefaultSitesMerged,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { uniqBy } from 'lodash-es';
import { useMemo } from 'react';

import { AddressOption, AddressType } from '~components/Order/SiteSelection/types';
import { Site } from '~hooks/useSites/models';
import connection from '~services/connectionModule';
import { Nullable } from '~types/Nullable';

interface UseDefaultSitesQueryProps {
  companyId?: Nullable<string>;
  orderId?: Nullable<string>;
  projectId?: Nullable<string>;
}

interface GroupedDefaultSites {
  pickup: AddressOption[];
  drop_off: AddressOption[];
}

const EMPTY_GROUPED_DEFAULT_SITES: GroupedDefaultSites = { pickup: [], drop_off: [] };

const toAddressOption = (row: Default_Site_Read_Nested): AddressOption => {
  // The merged endpoint returns full site_read_nested (geofence included) inline,
  // so we parse it directly with no per-site getSiteById hydration call.
  const site = Site.parseNested(row);

  return {
    type: AddressType.DefaultSites,
    lat: site.lat ?? '',
    lng: site.lon ?? '',
    siteId: site.id,
    id: site.id,
    name: site.name,
    externalId: site.externalId,
    geofence: site.nextBillionGeofence,
    companyShare: site.companyShare,
    discardedAt: site.discardedAt ?? null,
    sources: row.sources,
  };
};

const toGroupedDefaultSites = (
  data: Default_Sites_Grouped_Read['data'],
): GroupedDefaultSites => ({
  pickup: data.pickup.map(toAddressOption),
  drop_off: data.drop_off.map(toAddressOption),
});

const hasSource = (
  option: AddressOption,
  type: 'Order' | 'Project',
  id?: Nullable<string>,
) => option.sources?.some((s) => s.type === type && (!id || s.id === id)) ?? false;

/**
 * Fetches an order's or project's default pickup + drop_off sites from the
 * merged /default_sites endpoints.
 *
 * - project form (no orderId): one project query keyed on projectId.
 * - order-edit, project unchanged: one order query keyed on orderId — its merged
 *   response already includes the order's saved project's sites, so no second call.
 * - order-edit, project switched client-side: the selected project's sites are no
 *   longer represented in the order response's `sources`, so a supplemental project
 *   query fires for the new project and is merged with the order's own sites. This
 *   keeps the one-request common case while still refreshing when the user reassigns
 *   an unsaved order to a different project (which the order endpoint can't reflect
 *   until save).
 */
export default function useDefaultSitesQuery({
  companyId,
  orderId,
  projectId,
}: UseDefaultSitesQueryProps) {
  const orderQuery = useQuery({
    queryKey: ['defaultSites', 'order', orderId],
    queryFn:
      companyId && orderId
        ? async ({ signal }): Promise<GroupedDefaultSites> => {
            try {
              const response =
                await getV1CompaniesCompanyIdOrdersOrderIdDefaultSitesMerged({
                  path: { 'company-id': companyId, 'order-id': orderId },
                  signal,
                });
              return toGroupedDefaultSites(response.data.data);
            } catch (error) {
              connection.handleRequestError(
                error,
                t('error_messages.sites.failed_to_fetch_default_sites'),
              );
              throw error;
            }
          }
        : skipToken,
  });

  // On initial order-edit the selected project equals the order's saved project,
  // so its sites are already merged into the order response. Only once the user
  // switches to a project that isn't represented there do we fetch separately.
  const selectedProjectMergedIntoOrder = useMemo(() => {
    if (!projectId || !orderQuery.data) return false;
    return [...orderQuery.data.pickup, ...orderQuery.data.drop_off].some((option) =>
      hasSource(option, 'Project', projectId),
    );
  }, [projectId, orderQuery.data]);

  const projectSwitchedOnOrder = Boolean(
    orderId && projectId && orderQuery.isSuccess && !selectedProjectMergedIntoOrder,
  );
  const needsProjectQuery = Boolean(
    companyId && projectId && (!orderId || projectSwitchedOnOrder),
  );

  const projectQuery = useQuery({
    queryKey: ['defaultSites', 'project', projectId],
    queryFn:
      needsProjectQuery && companyId && projectId
        ? async ({ signal }): Promise<GroupedDefaultSites> => {
            try {
              const response =
                await getV1CompaniesCompanyIdProjectsProjectIdDefaultSitesMerged({
                  path: { 'company-id': companyId, 'project-id': projectId },
                  signal,
                });
              return toGroupedDefaultSites(response.data.data);
            } catch (error) {
              connection.handleRequestError(
                error,
                t('error_messages.sites.failed_to_fetch_default_sites'),
              );
              throw error;
            }
          }
        : skipToken,
  });

  const defaultSites = useMemo<GroupedDefaultSites>(() => {
    if (!orderId) {
      return projectQuery.data ?? EMPTY_GROUPED_DEFAULT_SITES;
    }

    const order = orderQuery.data ?? EMPTY_GROUPED_DEFAULT_SITES;
    if (!projectSwitchedOnOrder) {
      return order;
    }

    // Project switched: drop the (now stale) saved-project sites and keep the
    // order's own sites, then layer in the newly-selected project's sites.
    const project = projectQuery.data ?? EMPTY_GROUPED_DEFAULT_SITES;
    return {
      pickup: uniqBy(
        [...order.pickup.filter((o) => hasSource(o, 'Order')), ...project.pickup],
        'id',
      ),
      drop_off: uniqBy(
        [...order.drop_off.filter((o) => hasSource(o, 'Order')), ...project.drop_off],
        'id',
      ),
    };
  }, [orderId, orderQuery.data, projectQuery.data, projectSwitchedOnOrder]);

  return {
    defaultSites,
    isFetching: orderQuery.isFetching || projectQuery.isFetching,
  };
}
